<template>
  <modal
    v-if="history.ai_tutor && history.ai_tutor.length"
    class="assignment-ai-history"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <template #title>
      <div :style="{ display: 'flex', gap: '12px' }">
        <span :style="{ color: '#121131', fontSize: '20px', lineHeight: '28px' }">{{
          $t("assignments.modals.assignment_history").split(" - ")[0]
        }}</span>
        <span :style="{ color: '#757575', fontSize: '18px', lineHeight: '28px' }">{{
          userInfo["user_name"] || ""
        }}</span>
        <span :style="{ color: '#757575', fontSize: '14px', lineHeight: '28px' }">{{
          userInfo["user_email"] || ""
        }}</span>
      </div>
    </template>
    <template v-slot:body>
      <el-table
        v-if="history.ai_tutor && history.ai_tutor.length"
        :data="history.ai_tutor"
        :default-sort="{ prop: 'timestamp', order: 'descending' }"
        :empty-text="$t('common.no_values')"
        class="assignment-history"
        header-row-class-name="assignment-header"
        v-loading="loading"
        max-height="75vh"
      >
        <el-table-column
          :label="$t('ai_tutor.title.assignment_completed')"
          prop="timestamp"
          sortable
          :sort-method="sortDateTime"
          min-width="200"
        >
          <template slot-scope="scope">
            <div class="date-completed">
              <p class="date">
                {{ dateFormatter(scope.row.timestamp) }}
              </p>
              <div class="tag-wrapper">
                <chip-rounded
                  v-show="scope.row.status"
                  :class="getChipClass(scope.row.status)"
                  class="chip-rounded--small"
                >
                  {{ scope.row.status }}
                </chip-rounded>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          :label="`${formatText($t('classes.classes_details.assessment.table.overall_score'))}`"
          align="center"
          sortable
          prop="score"
          width="180"
        >
          <template slot-scope="scope"> {{ scope.row.score }}% </template>
        </el-table-column>

        <el-table-column
          :label="`${formatText($t('ai_tutor.goals_completed'))}`"
          align="center"
          sortable
          prop="goals_completed"
          width="200"
        >
          <template slot-scope="scope"> {{ scope.row.goals_completed }}/{{ scope.row.total_goals }} </template>
        </el-table-column>

        <el-table-column
          :label="`${formatText($t('ai_tutor.label_table_performance.pronunciation'))}`"
          align="center"
          sortable
          prop="pronunciation"
          width="160"
        >
          <template slot-scope="scope">
            <span class="text-tranform-cap" :style="{ color: getColorLevelAI(scope.row.pronunciation) }">{{
              getTextScoreLevel(scope.row.pronunciation) || "--"
            }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="`${formatText($t('ai_tutor.label_table_performance.vocabulary'))}`"
          align="center"
          sortable
          prop="vocabulary"
          width="150"
        >
          <template slot-scope="scope">
            <span class="text-tranform-cap" :style="{ color: getColorLevelAI(scope.row.vocabulary) }">{{
              getTextScoreLevel(scope.row.vocabulary) || "--"
            }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="`${formatText($t('ai_tutor.label_table_performance.grammar'))}`"
          align="center"
          sortable
          prop="grammar"
          width="140"
        >
          <template slot-scope="scope">
            <span class="text-tranform-cap" :style="{ color: getColorLevelAI(scope.row.grammar) }"
              >{{ getTextScoreLevel(scope.row.grammar) || "--" }}
            </span></template
          >
        </el-table-column>
        <el-table-column
          :label="`${formatText($t('ai_tutor.hints_used'))}`"
          align="center"
          sortable
          prop="hints_used"
          width="140"
        >
          <template slot-scope="scope"> {{ formatHintUsed(scope.row.hints_used) }} </template>
        </el-table-column>
        <el-table-column
          :label="`${formatText($t('ai_tutor.n_input'))}`"
          align="center"
          sortable
          prop="n_input"
          width="140"
        >
          <template slot-scope="scope"> {{ formatHintUsed(scope.row.n_input) }} </template>
        </el-table-column>
        <el-table-column
          :label="`${$t('ai_tutor.time_spent')}`"
          align="center"
          sortable
          prop="time_spent"
          width="140"
        >
          <template slot-scope="scope"> {{ msToTime(scope.row.time_spent * 1000) }} </template>
        </el-table-column>
        <el-table-column
          :label="formatText($t('classes.classes_details.conversation.table.download_transcript'))"
          align="center"
          min-width="140"
        >
          <template slot-scope="scope">
            <el-button type="primary" size="mini" @click="downloadTranscript(scope.row.conversation_id)">
              <i class="el-icon-download"></i>
            </el-button>
          </template>
        </el-table-column>
        <!-- <el-table-column
          :label="formatText($t('classes.classes_details.conversation.table.report_issue'))"
          align="center"
          min-width="110"
        >
          <template slot-scope="scope"> {{ scope.row.assignment_detail_id }} </template>
        </el-table-column> -->
      </el-table>
    </template>
  </modal>
</template>

<script>
import axios from "@/utils/axios";
import { isNil, unless } from "ramda";
import { mapGetters } from "vuex";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import Modal from "@/components/common/Modal";
import ChipRounded from "@/components/common/ChipRounded.vue";
import { SUBMISSION_LATE, SUBMISSION_MISSED } from "@/constants/generic";
import { colorLevelAI } from "@/constants/elsaAI";

export default {
  components: {
    ChipRounded,
    Modal,
  },
  props: {
    userInfo: {
      type: Object,
    },
    history: {
      type: Object,
    },
    assignmentType: {
      type: String,
    },
  },
  computed: {
    ...mapGetters({
      currentOrganization: "organization/currentOrganization",
    }),
    titleHistory() {
      return "history";
    },
  },
  data() {
    return {
      dateFormat: "dd/MM/yyyy HH:mm",
      loading: false,
    };
  },
  methods: {
    closeModal() {
      this.$emit("closed");
    },
    dateFormatter(date) {
      if (isNaN(Date.parse(date))) {
        return date;
      }
      const zonedDate = utcToZonedTime(date, this.currentOrganization.timezone);
      return format(zonedDate, this.dateFormat);
    },
    getChipClass(status) {
      switch (status) {
        case SUBMISSION_MISSED:
          return "chip-rounded--red";
        case SUBMISSION_LATE:
          return "chip-rounded--orange";
        default:
          return "";
      }
    },
    formatScore(score) {
      if (isNaN(score)) {
        return "--";
      } else if (score === 0) {
        return "0%";
      } else {
        return Math.round(score) + "%";
      }
    },

    formatText(text) {
      if (!text) return "";
      return text.replace(/\(\w*\)/gm, "").trim();
    },
    formatHintUsed(number) {
      if (number === undefined) return "";
      return Math.round(number);
    },
    msToTime(duration) {
      if (!duration) return "--";
      let seconds = Math.floor((duration / 1000) % 60);
      let minutes = Math.floor((duration / (1000 * 60)) % 60);
      let hours = Math.floor((duration / (1000 * 60 * 60)) % 24);

      hours = hours < 10 ? "0" + hours : hours;
      minutes = minutes < 10 ? "0" + minutes : minutes;
      seconds = seconds < 10 ? "0" + seconds : seconds;

      return hours + ":" + minutes + ":" + seconds;
    },
    getColorLevelAI(level) {
      return '#121131'
      // if (!level) return "#000";
      // return colorLevelAI[level.toLowerCase()];
    },
    sortDateTime(a, b) {
      if (!a.timestamp || !b.time_spent) return;

      return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    },
    sortScore(a, b) {
      if (!a.score || !b.score) return;

      return a.score - b.score;
    },
    sortObjective(a, b) {
      if (!a.score || !b.score) return;

      return a.score - b.score;
    },
    async downloadTranscript(conversation_id) {
      if (conversation_id == null) {
        return;
      }
      try {
        this.loading = true;
        const groupId = this.$route.params.classId;
        const url = `v1/groups/${groupId}/role-play-conversation/${conversation_id}/download`;
        const response = await axios({
          method: "GET",
          url: url,
        });
        const blod = new Blob([response.data], { type: "text/csv" });
        const fileName = `${conversation_id}.csv`;

        // Create a link element and trigger the download
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blod);
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        throw error;
      } finally {
        this.loading = false;
      }
    },
    getTextScoreLevel(scoreText) {
      if(!scoreText) return '--'
      switch (scoreText.toLowerCase()) {
        case 'elementary':
          return this.$t("ai_tutor.score_range.elementary");
        case 'intermediate':
          return this.$t("ai_tutor.score_range.intermediate");
        case 'advanced':
          return this.$t("ai_tutor.score_range.advanced");
        case 'none':
          return '--'
        default:
          return scoreText;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.assignment-history {
  padding-bottom: 10px !important;
}
.actions {
  align-items: flex-start;
  .el-button {
    padding: 12px;
  }
}
.assignment-ai-history {
  ::v-deep .el-dialog.elsa-modal {
    width: 90%;
  }
}
.date-completed {
  display: flex;
  align-items: center;
  .date {
    width: 190px;
  }
  .tag-wrapper {
    min-width: 50px;
    margin-left: 10px;
  }
}
.text-tranform-cap {
  text-transform: capitalize;
}

::v-deep .el-table .el-table__header-wrapper .assignment-header .is-leaf {
  background-color: #f9fbfd !important;
}

::v-deep .assignment-history .el-table__header-wrapper {
  padding: 0;
  margin: 0;
  min-height: 70px;
}

::v-deep .chip-rounded--small {
  min-width: 60px;
}

::v-deep .assignment-header .cell {
  display: flex;
  justify-content: center;
  align-items: center;
  white-space: pre-wrap;
}
</style>
